/**
 * Lightweight playback highlight (SPP-654).
 * Avoids setState/decorate on every timeupdate — that froze large transcripts.
 * Word + caption chrome update via CSS rules (survive React remounts when pin
 * flips content-visibility). While playing, sync from an rAF loop so short
 * words are not skipped by sparse timeupdate events.
 */

import { Path } from 'slate';
import {
  CHUNK_PLAYBACK_PREFETCH_LEAD_SEC,
  CHUNK_VIDEO_SEEK_IGNORE_MS,
  findCaptionBlockForTime,
  findCaptionIndexForTime,
  findWordStartForTime,
} from 'utils/transcriptChunk.util';
import {
  captionStartForTime,
  transcriptVisibility,
  transcriptVisibilityKey,
} from './transcriptVisibility';
import {
  chunkPerfLog,
  beginChunkSeekPerf,
  chunkSeekPerf,
  endChunkSeekPerf,
  getTranscriptNodes,
  transcriptChunkStore,
} from './TranscriptChunkStore';

const STYLE_ID = 'spp-playback-highlight-style';

type Cache = {
  captionIndex: number;
  wordIndex: number;
  captionStart: number | null;
  wordStart: number | null;
};

const cache: Cache = {
  captionIndex: 0,
  wordIndex: 0,
  captionStart: null,
  wordStart: null,
};

let playLoopRaf = 0;
let scheduleRaf = 0;
let pendingLinePlayScroll = false;
/** Blocks progress-bar seek handling while caption-row play sets video.currentTime. */
let linePlaySeekGuardUntil = 0;

const armLinePlaySeekGuard = () => {
  extendVideoSeekIgnore();
  linePlaySeekGuardUntil = performance.now() + CHUNK_VIDEO_SEEK_IGNORE_MS;
};

export const isLinePlaySeekGuardActive = () =>
  performance.now() < linePlaySeekGuardUntil;
let pendingTime: number | null = null;
let pendingSyncOpts: SyncOpts | undefined;
let lastFollow = true;
let lastActiveChunkId: string | null = null;
/** Avoid re-sliding the DWCM window on every rAF tick for the same ordinal. */
let lastFollowOrdinal: number | null = null;
/** Coalesce duplicate chunk-ready highlight sync on the same editor mount. */
let lastChunkReadyHighlightRevision = -1;
let getIsEditMode: (() => boolean) | null = null;
let getIsFollowing: (() => boolean) | null = null;
let onCaptionChange:
  | ((info: { captionStart: number; path: Path }) => void)
  | null = null;

type Block = {
  data?: { start?: number; end?: number };
  children?: { text?: string; data?: { dataStart?: number; dataEnd?: number } }[];
};

type SyncOpts = {
  isFollowing?: boolean;
  /** User-initiated seek (progress bar, skip, transcript jump) — load target chunk/window. */
  seek?: boolean;
  /** Progress bar drag in progress — snap scroll only, load on release. */
  sliding?: boolean;
  /** Slate chunk/window just mounted — avoid smooth re-center if already visible. */
  chunkReady?: boolean;
  /** Caption-row play — never use progress-bar seek load path. */
  linePlay?: boolean;
  /** Seek already finished — highlight/scroll only; do not re-arm window load. */
  afterSeek?: boolean;
  /** Center the active caption after seek / line play. */
  forceScrollToCenter?: boolean;
};

type HighlightOpts = {
  allowScroll?: boolean;
  scrollBehavior?: ScrollBehavior;
  /** Skip follow scroll when the caption row is already in the viewport. */
  skipScrollIfVisible?: boolean;
  /** Center even when caption did not change (e.g. line play / resume). */
  forceScrollToCenter?: boolean;
};

/** Prefer the mounted chunk's nodes so indices/paths match the live Slate tree. */
const getPlaybackBlocks = (): Block[] | undefined => {
  if (transcriptChunkStore.isEnabled()) {
    const activeId = transcriptChunkStore.getActiveChunkId();
    if (activeId !== lastActiveChunkId) {
      lastActiveChunkId = activeId;
      cache.captionIndex = 0;
      cache.wordIndex = 0;
      cache.captionStart = null;
      cache.wordStart = null;
    }
    // DWCM merges 2–3 chunks in one Editor — use live tree, not start chunk only.
    if (transcriptChunkStore.isDwcm()) {
      const live = (window as any)?.Editor?.children as Block[] | undefined;
      if (live?.length) return live;
      return transcriptChunkStore.getMergedWindowNodes() as Block[];
    }
    return transcriptChunkStore.getActiveNodes() as Block[];
  }
  return (window as any)?.Editor?.children as Block[] | undefined;
};

/** Blocks for highlight at `time` — live editor when ready, else store chunk/window. */
const getBlocksForHighlightAtTime = (time: number): Block[] | undefined => {
  if (!transcriptChunkStore.isEnabled()) {
    return (
      getPlaybackBlocks() ??
      (getTranscriptNodes() as Block[] | undefined) ??
      ((window as any)?.Editor?.children as Block[] | undefined)
    );
  }

  const resolved = transcriptChunkStore.resolveChunkForTime(time, {
    playing: false,
  });
  if (!resolved) return undefined;

  if (resolved.ready && transcriptChunkStore.isEditorReady()) {
    const live = getPlaybackBlocks();
    if (live?.length) return live;
  }

  if (transcriptChunkStore.isDwcm()) {
    const w = transcriptChunkStore.getActiveWindow();
    if (
      w &&
      resolved.meta.ordinal >= w.startOrdinal &&
      resolved.meta.ordinal <= w.endOrdinal
    ) {
      const merged = transcriptChunkStore.getMergedWindowNodes() as Block[];
      if (merged.length) return merged;
    }
  }

  const chunkNodes = transcriptChunkStore.getChunkNodes(
    resolved.meta.chunkId,
  ) as Block[];
  return chunkNodes?.length ? chunkNodes : undefined;
};

const isCaptionInTranscriptViewport = (
  shell: HTMLElement,
  root: HTMLElement,
) => {
  const rootRect = root.getBoundingClientRect();
  const elRect = shell.getBoundingClientRect();
  return elRect.bottom > rootRect.top && elRect.top < rootRect.bottom;
};

type HighlightHit = {
  captionIndex: number;
  captionStart: number;
  wordStart: number | null;
};

const captionStartsMatch = (
  a: number | null | undefined,
  b: number | null | undefined,
) => a != null && b != null && String(a) === String(b);

/** Resolve the one caption row for `time` — full doc when chunked, else live editor. */
const resolvePlaybackHit = (
  time: number,
  localBlocks?: Block[],
): HighlightHit | null => {
  if (transcriptChunkStore.isEnabled()) {
    const resolved = transcriptChunkStore.resolvePlaybackCaptionAtTime(time);
    if (!resolved) return null;

    let captionIndex = resolved.globalIndex;
    let wordStart = resolved.wordStart;

    if (localBlocks?.length) {
      const localIndex = localBlocks.findIndex((block) =>
        captionStartsMatch(block?.data?.start, resolved.captionStart),
      );
      if (localIndex >= 0) {
        captionIndex = localIndex;
        const localWord = findWordStartForTime(localBlocks[localIndex], time);
        if (localWord != null) wordStart = localWord;
      }
    }

    cache.captionIndex = captionIndex;
    return {
      captionIndex,
      captionStart: resolved.captionStart,
      wordStart,
    };
  }

  const blocks =
    localBlocks ??
    getPlaybackBlocks() ??
    (getTranscriptNodes() as Block[] | undefined);
  if (!blocks?.length) return null;

  const block = findCaptionBlockForTime(blocks, time);
  if (!block) return null;
  const captionStart = block.data?.start;
  if (typeof captionStart !== 'number') return null;

  const idx = findCaptionIndexForTime(blocks, time);
  cache.captionIndex = idx;
  return {
    captionIndex: idx,
    captionStart,
    wordStart: findWordStartForTime(block, time),
  };
};

const scrollCaptionToFollowPosition = (
  captionStart: number,
  opts?: HighlightOpts,
) => {
  if (opts?.allowScroll === false || shouldSuppressPlaybackFollowScroll()) return;
  if (!lastFollow || !transcriptChunkStore.isEditorReady()) return;

  const shell = document.querySelector(
    `div[data-slate-node="element"][data-start="${playbackDataAttr(
      captionStart,
    )}"]`,
  ) as HTMLElement | null;
  if (!shell) return;

  const root = document.querySelector(
    '[data-transcript-scroll-root]',
  ) as HTMLElement | null;
  const alreadyVisible =
    opts?.skipScrollIfVisible &&
    root &&
    isCaptionInTranscriptViewport(shell, root);
  if (alreadyVisible && !opts?.forceScrollToCenter) return;

  const align: 'center' | 'start' =
    root &&
    preferStartAlignForPlaybackFollow(root) &&
    isCaptionNearViewportStart(shell, root)
      ? 'start'
      : 'center';

  alignCaptionInTranscriptViewport(
    shell,
    opts?.scrollBehavior ?? 'smooth',
    align,
  );
};

const applyHighlightFromHit = (hit: HighlightHit, opts?: HighlightOpts) => {
  const captionChanged = hit.captionStart !== cache.captionStart;
  const wordChanged = hit.wordStart !== cache.wordStart;

  if (captionChanged) {
    cache.captionStart = hit.captionStart;

    const key = transcriptVisibilityKey(hit.captionStart);
    transcriptVisibility.pin(key, 'playback');

    onCaptionChange?.({
      captionStart: hit.captionStart,
      path: [hit.captionIndex] as Path,
    });
  }

  const mayFollowScroll = opts?.allowScroll !== false;
  if (opts?.forceScrollToCenter && mayFollowScroll) {
    scrollCaptionToFollowPosition(hit.captionStart, opts);
  } else if (captionChanged && mayFollowScroll) {
    scrollCaptionToFollowPosition(hit.captionStart, opts);
  }

  if (wordChanged || captionChanged) {
    cache.wordStart = hit.wordStart;
    setHighlightRules(cache.captionStart, cache.wordStart);
    syncTranscriptPlaybackChrome();
  }
};

const applyHighlightAtTime = (
  time: number,
  children: Block[],
  opts?: HighlightOpts,
) => {
  const hit = resolvePlaybackHit(time, children);
  if (!hit) return;
  applyHighlightFromHit(hit, opts);
};

/** Stable attribute string for CSS/DOM selectors (float-safe). */
export const playbackDataAttr = (value: number | string | null | undefined) =>
  value == null || value === '' ? '' : String(value);

/** Active caption start from the playback highlight loop (single source of truth). */
export const getPlaybackActiveCaptionStart = (): number | null =>
  cache.captionStart;

export const isPlaybackActiveCaption = (
  captionStart: number | null | undefined,
): boolean => {
  const active = cache.captionStart;
  return (
    active != null &&
    typeof captionStart === 'number' &&
    active === captionStart
  );
};

const syncPlaybackPlayingAttr = (playing: boolean) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.transcriptPlaying = playing ? '1' : '0';
};

/** Sync html[data-transcript-playing] for CSS play/pause icon swap. */
export const syncTranscriptPlaybackChrome = () => {
  const video = (window as any).Video as HTMLMediaElement | undefined | null;
  syncPlaybackPlayingAttr(!!video && !video.paused && !video.ended);
};

const ensureStyleEl = () => {
  if (typeof document === 'undefined') return null;
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  return style;
};

const setHighlightRules = (
  captionStart: number | null,
  wordStart: number | null,
) => {
  const style = ensureStyleEl();
  if (!style) return;

  const captionAttr = playbackDataAttr(captionStart);
  const wordAttr = playbackDataAttr(wordStart);

  const captionCss =
    captionAttr === ''
      ? ''
      : `
    div[data-slate-node="element"] .playBackPause {
      display: none;
    }
    div[data-slate-node="element"][data-start="${captionAttr}"] {
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 2rem;
      background-color: rgb(240, 240, 255);
      border-bottom: 3px solid rgb(85, 81, 255);
    }
    div[data-slate-node="element"][data-start="${captionAttr}"] .playBackPlay path {
      fill: #9b99ff !important;
    }
    div[data-slate-node="element"][data-start="${captionAttr}"] .playBackPause {
      display: none;
      background-color: #5551FF !important;
    }
    html[data-transcript-playing="1"] div[data-slate-node="element"][data-start="${captionAttr}"] .playBackPlay {
      display: none;
    }
    html[data-transcript-playing="1"] div[data-slate-node="element"][data-start="${captionAttr}"] .playBackPause {
      display: inline-block;
    }
  `;

  const wordCss =
    wordAttr === ''
      ? ''
      : `
    span.word[data-slate-leaf="true"][data-start="${wordAttr}"] {
      color: #5551FF;
      text-shadow: 0 0 1px #5551FF;
    }
  `;

  style.textContent = `${captionCss}${wordCss}`;
};

const SMOOTH_FOLLOW_SCROLL_MS = 800;
const CENTER_FOLLOW_EPSILON_PX = 4;
/** When booted on chunk 0 with scroll near top, pin rows to viewport start — not center. */
const FIRST_CHUNK_START_SCROLL_MAX_PX = 64;

const shouldSuppressPlaybackFollowScroll = () =>
  transcriptChunkStore.isUserScrollNavigationActive() ||
  !!transcriptChunkStore.getScrollPinnedChunkId() ||
  transcriptChunkStore.isFindJumpInProgress();

const preferStartAlignForPlaybackFollow = (
  root: HTMLElement | null,
): boolean => {
  if (!root) return false;
  if (root.scrollTop > FIRST_CHUNK_START_SCROLL_MAX_PX) return false;
  const w = transcriptChunkStore.getActiveWindow();
  if (!w || w.startOrdinal !== 0) return false;
  return true;
};

const isCaptionNearViewportStart = (
  shell: HTMLElement,
  root: HTMLElement,
) => {
  const rootRect = root.getBoundingClientRect();
  const elRect = shell.getBoundingClientRect();
  return elRect.top >= rootRect.top - 4 && elRect.top <= rootRect.top + 48;
};

/** Scroll caption row to start or center inside the transcript scroll root. */
const alignCaptionInTranscriptViewport = (
  shell: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
  align: 'center' | 'start' = 'center',
) => {
  if (shouldSuppressPlaybackFollowScroll()) return;

  const root = document.querySelector(
    '[data-transcript-scroll-root]',
  ) as HTMLElement | null;

  if (root && root.contains(shell)) {
    const rootRect = root.getBoundingClientRect();
    const elRect = shell.getBoundingClientRect();

    if (align === 'start') {
      const delta = elRect.top - rootRect.top;
      if (Math.abs(delta) < 4) return;
      transcriptChunkStore.markProgrammaticScroll(
        behavior === 'smooth' ? SMOOTH_FOLLOW_SCROLL_MS : 250,
      );
      if (behavior === 'smooth') {
        root.scrollBy({ top: delta, behavior: 'smooth' });
      } else {
        root.scrollTop += delta;
      }
      return;
    }

    const delta =
      elRect.top +
      elRect.height / 2 -
      (rootRect.top + rootRect.height / 2);
    if (Math.abs(delta) < CENTER_FOLLOW_EPSILON_PX) return;

    transcriptChunkStore.markProgrammaticScroll(
      behavior === 'smooth' ? SMOOTH_FOLLOW_SCROLL_MS : 250,
    );
    if (behavior === 'smooth') {
      root.scrollBy({ top: delta, behavior: 'smooth' });
    } else {
      root.scrollTop += delta;
    }
    return;
  }

  transcriptChunkStore.markProgrammaticScroll(
    behavior === 'smooth' ? SMOOTH_FOLLOW_SCROLL_MS : 250,
  );
  shell.scrollIntoView({ block: align, behavior });
};

const maybePrefetchDwcmWindow = (time: number, currentOrdinal: number) => {
  const index = transcriptChunkStore.getIndex();
  const next = index[currentOrdinal + 1];
  if (!next) return;

  const w = transcriptChunkStore.getActiveWindow();
  if (!w || next.ordinal <= w.endOrdinal) return;
  if (time < next.startTime - CHUNK_PLAYBACK_PREFETCH_LEAD_SEC) return;

  transcriptChunkStore.ensureWindowContainsOrdinal(next.ordinal);
};

const ensureDwcmWindowForOrdinal = (ordinal: number) => {
  if (lastFollowOrdinal === ordinal) return;
  lastFollowOrdinal = ordinal;
  transcriptChunkStore.ensureWindowContainsOrdinal(ordinal);
};

const resolveIsFollowingPref = (): boolean =>
  getIsFollowing?.() ?? lastFollow;

const resolveFollow = (opts?: { isFollowing?: boolean }) => {
  if (opts?.isFollowing !== undefined) {
    lastFollow = opts.isFollowing !== false;
    return lastFollow;
  }
  lastFollow = resolveIsFollowingPref() !== false;
  return lastFollow;
};

/** Cross-chunk seek — defer live-editor scroll until window/chunk is mounted. */
const shouldDeferSeekLiveEditorSync = (opts?: SyncOpts) =>
  !!opts?.seek && !!transcriptChunkStore.getPlaybackSeekTarget();

/** CSS highlight from store cache — works before Slate remounts on seek. */
const applyImmediateSeekHighlight = (time: number) => {
  const local = getBlocksForHighlightAtTime(time);
  const hit = resolvePlaybackHit(time, local);
  if (!hit) return;
  applyHighlightFromHit(hit, { allowScroll: false });
};

/** Progress bar owns this seek — ignore duplicate video seeking/seeked handlers. */
let progressBarSeekDriving = false;
let ignoreVideoSeekUntil = 0;

export const markProgressBarSeekDriving = () => {
  progressBarSeekDriving = true;
  ignoreVideoSeekUntil = performance.now() + CHUNK_VIDEO_SEEK_IGNORE_MS;
};

export const clearProgressBarSeekDriving = () => {
  progressBarSeekDriving = false;
};

export const extendVideoSeekIgnore = () => {
  ignoreVideoSeekUntil = performance.now() + CHUNK_VIDEO_SEEK_IGNORE_MS;
};

export const shouldIgnoreVideoSeekEvent = () => {
  if (progressBarSeekDriving || performance.now() < ignoreVideoSeekUntil) {
    return true;
  }
  // Progress-bar already armed a target — ignore stale seeking/seeked from
  // superseded media seeks (rapid clicks) so we don't reload the wrong window.
  if (
    transcriptChunkStore.getPlaybackSeekTarget() &&
    !transcriptChunkStore.isPlaybackSeekSliding()
  ) {
    return true;
  }
  return false;
};

/** True while progress-bar seek is loading a distant chunk (keep bar at user position). */
export const isProgressBarSeekPending = () => {
  if (progressBarSeekDriving) return true;
  if (!transcriptChunkStore.isEnabled() || isLinePlaySeekGuardActive()) {
    return false;
  }
  if (transcriptChunkStore.isPlaybackSeekSliding()) {
    if (
      !transcriptChunkStore.getPlaybackSeekTarget() &&
      transcriptChunkStore.isEditorReady()
    ) {
      return false;
    }
    return true;
  }
  const target = transcriptChunkStore.getPlaybackSeekTarget();
  if (!target) return false;
  return !transcriptChunkStore.isSeekSatisfiedAtTime(target.time);
};

const isEditModeActive = () => getIsEditMode?.() ?? false;

const runSync = (time: number, opts?: SyncOpts) => {
  resolveFollow(opts);

  // SPP-655: do not thrash chunk loads during playback. If the playhead is in
  // another chunk, pause until that chunk’s editor is ready (skeleton load).
  const video = (window as any).Video as HTMLMediaElement | undefined | null;
  const playing = !!video && !video.paused && !video.ended;
  syncTranscriptPlaybackChrome();

  if (transcriptChunkStore.isEnabled()) {
    if (transcriptChunkStore.isSaveInFlight() && !opts?.seek && !opts?.linePlay) {
      const children = getBlocksForHighlightAtTime(time);
      if (children?.length) {
        applyHighlightAtTime(time, children, {
          allowScroll: false,
          skipScrollIfVisible: true,
        });
      }
      return;
    }

    // Scroll freeze: do not resolve/load chunks (avoids compute storm).
    // Progress-bar seek is explicit navigation — always honor it.
    if (
      transcriptChunkStore.isScrolling() &&
      !opts?.seek &&
      !opts?.linePlay &&
      !opts?.chunkReady
    ) {
      return;
    }

    if (
      !opts?.seek &&
      !opts?.linePlay &&
      !opts?.chunkReady &&
      shouldSuppressPlaybackFollowScroll() &&
      !isEditModeActive()
    ) {
      const children = getBlocksForHighlightAtTime(time);
      if (children?.length) {
        applyHighlightAtTime(time, children, {
          allowScroll: false,
          skipScrollIfVisible: true,
        });
      }
      return;
    }

    if (opts?.linePlay) {
      transcriptChunkStore.clearScrollPinnedChunk();
      transcriptChunkStore.clearUserScrollNavigation();
      transcriptChunkStore.cancelPlaybackSeek('transcript-line-play');
      armLinePlaySeekGuard();
      cache.captionIndex = 0;
      cache.wordIndex = 0;

      const inMountedWindow = transcriptChunkStore.isSeekSatisfiedAtTime(time);

      if (transcriptChunkStore.isDwcm() && !inMountedWindow) {
        const resolved = transcriptChunkStore.resolveChunkForTime(time, {
          playing: true,
        });
        if (resolved) {
          transcriptChunkStore.ensureWindowContainsOrdinal(
            resolved.meta.ordinal,
            'line-play-window',
          );
        }
      }
      applyImmediateSeekHighlight(time);

      // Caption already on screen — highlight only; never progress-bar seek load.
      if (inMountedWindow) {
        const blocks = getBlocksForHighlightAtTime(time);
        if (blocks?.length) {
          applyHighlightAtTime(time, blocks, {
            allowScroll: lastFollow,
            forceScrollToCenter: lastFollow,
            scrollBehavior: 'smooth',
          });
        }
        pendingLinePlayScroll = false;
        return;
      }
    }

    if (opts?.seek && !opts?.afterSeek) {
      cache.captionIndex = 0;
      cache.wordIndex = 0;
      transcriptChunkStore.armPlaybackSeek(time);
      if (opts.sliding !== undefined) {
        transcriptChunkStore.setPlaybackSeekSliding(opts.sliding);
      }
      if (!transcriptChunkStore.isPlaybackSeekSliding()) {
        transcriptChunkStore.invalidateEditorWindowReady();
        transcriptChunkStore.ensureChunkForTime(time, {
          playing,
          seek: true,
        });
      }
      applyImmediateSeekHighlight(time);
      chunkSeekPerf('highlight-css', {
        time,
        sliding: opts.sliding ?? false,
      });
    }

    const resolved = transcriptChunkStore.resolveChunkForTime(time, {
      playing,
    });
    const pinned = transcriptChunkStore.getScrollPinnedChunkId();

    // User scrolled to another chunk — never yank back to playhead chunk.
    // Clear the pin once the playhead naturally enters the pinned chunk.
    if (pinned) {
      if (resolved?.meta.chunkId === pinned) {
        transcriptChunkStore.clearScrollPinnedChunk();
      } else if (
        !opts?.seek &&
        !opts?.afterSeek &&
        !opts?.linePlay &&
        !opts?.chunkReady
      ) {
        if (playing && video && !isEditModeActive()) {
          try {
            video.pause();
          } catch {
            // ignore
          }
        }
        if (!isEditModeActive()) {
          return;
        }
      }
    }

    if (resolved?.needsLoad) {
      if (opts?.seek || opts?.linePlay) {
        return;
      }
      if (!opts?.chunkReady) {
        if (!playing) {
          return;
        }
        if (transcriptChunkStore.isDwcm()) {
          ensureDwcmWindowForOrdinal(resolved.meta.ordinal);
          if (video && playing && !isEditModeActive()) {
            transcriptChunkStore.markPlaybackPausedForLoad(
              resolved.meta.chunkId,
            );
            try {
              video.pause();
            } catch {
              // ignore
            }
          }
          return;
        }
        const status = transcriptChunkStore.requestChunkLoad(
          resolved.meta.chunkId,
        );
        if (video && (status === 'loading' || status === 'queued')) {
          transcriptChunkStore.markPlaybackPausedForLoad(resolved.meta.chunkId);
          try {
            video.pause();
          } catch {
            // ignore
          }
        }
        return;
      }
    }
    if (!resolved?.ready && !opts?.chunkReady) {
      return;
    }

    if (
      playing &&
      transcriptChunkStore.isDwcm() &&
      !opts?.seek &&
      resolved != null
    ) {
      maybePrefetchDwcmWindow(time, resolved.meta.ordinal);
    }
  }

  if (shouldDeferSeekLiveEditorSync(opts)) return;

  const children = getBlocksForHighlightAtTime(time);
  if (!children?.length) return;

  const followScrollAllowed =
    lastFollow && !shouldSuppressPlaybackFollowScroll();
  const linePlayFollowScroll = !!opts?.linePlay && followScrollAllowed;
  // Do not yank scroll when a chunk finishes loading while paused — only while
  // playing, on explicit seek, or caption-row play.
  const chunkReadyFollowScroll =
    followScrollAllowed &&
    (playing || !!opts?.seek || !!opts?.linePlay);
  const afterSeekFollowScroll =
    !!opts?.afterSeek && playing && followScrollAllowed;
  // Live follow must keep the active row centered even while it is still
  // on screen. Skip-if-visible is only for seek / paused remount so load
  // complete does not yank a row that is already in view.
  const skipScrollIfVisible = !(
    followScrollAllowed &&
    playing &&
    !opts?.seek &&
    !opts?.afterSeek
  );
  applyHighlightAtTime(time, children, {
    allowScroll: isEditModeActive()
      ? false
      : opts?.chunkReady
        ? chunkReadyFollowScroll || afterSeekFollowScroll
        : opts?.linePlay
          ? lastFollow
          : opts?.seek
            ? lastFollow
            : opts?.afterSeek
              ? afterSeekFollowScroll
              : followScrollAllowed,
    scrollBehavior:
      opts?.linePlay || followScrollAllowed ? 'smooth' : 'auto',
    forceScrollToCenter: linePlayFollowScroll,
    skipScrollIfVisible,
  });
  if (opts?.linePlay) {
    pendingLinePlayScroll = false;
  }
};

/** Play from caption row after stress scroll — sync bar, window, highlight, center scroll. */
export const startTranscriptLinePlayback = (time: number) => {
  if (
    transcriptChunkStore.isEnabled() &&
    !transcriptChunkStore.isTranscriptInteractionAllowed()
  ) {
    chunkPerfLog('line-play:blocked-not-ready', { time });
    return;
  }
  cancelScheduleRaf();
  transcriptChunkStore.cancelPlaybackSeek('transcript-line-play');
  armLinePlaySeekGuard();
  pendingLinePlayScroll = true;
  runSync(time, {
    isFollowing: resolveIsFollowingPref(),
    linePlay: true,
  });
};

const cancelScheduleRaf = () => {
  if (scheduleRaf) {
    cancelAnimationFrame(scheduleRaf);
    scheduleRaf = 0;
  }
  pendingTime = null;
  pendingSyncOpts = undefined;
};

/** Coalesce one-shot syncs (seek / pause) to one job per animation frame. */
export const schedulePlaybackHighlight = (
  time: number,
  opts?: SyncOpts,
) => {
  resolveFollow(opts);
  pendingTime = time;
  pendingSyncOpts = opts?.seek ? opts : pendingSyncOpts ?? opts;
  if (scheduleRaf) return;
  scheduleRaf = requestAnimationFrame(() => {
    scheduleRaf = 0;
    const t = pendingTime;
    const syncOpts = pendingSyncOpts;
    pendingTime = null;
    pendingSyncOpts = undefined;
    if (t == null) return;
    runSync(t, syncOpts);
  });
};

export const flushPlaybackHighlight = (
  time: number,
  opts?: SyncOpts,
) => {
  resolveFollow(opts);
  cancelScheduleRaf();
  const syncOpts =
    (pendingLinePlayScroll || isLinePlaySeekGuardActive()) &&
    !opts?.linePlay &&
    !opts?.chunkReady &&
    !opts?.seek
      ? { ...opts, linePlay: true }
      : opts;
  runSync(time, syncOpts);
};

/** Synchronous progress-bar / skip seek — snap while dragging, load on release. */
export const flushPlaybackSeek = (
  time: number,
  opts?: { isFollowing?: boolean; sliding?: boolean },
) => {
  if (!Number.isFinite(time) || time < 0) return;

  if (pendingLinePlayScroll || isLinePlaySeekGuardActive()) {
    flushPlaybackHighlight(time, { ...opts, linePlay: true });
    return;
  }

  if (opts?.sliding) {
    chunkSeekPerf('drag-preview', { time });
  } else {
    beginChunkSeekPerf({
      time,
      indexLength: transcriptChunkStore.getIndex().length,
      activeWindow: transcriptChunkStore.getActiveWindow(),
    });
  }

  if (!opts?.sliding) {
    transcriptChunkStore.cancelPendingWindowDirect();
    // Drag leaves sliding true; clear it before the in-window complete path
    // or line-play stays locked after a seek-bar release in the mounted window.
    if (transcriptChunkStore.isPlaybackSeekSliding()) {
      transcriptChunkStore.setPlaybackSeekSliding(false);
    }
  }

  if (
    !opts?.sliding &&
    transcriptChunkStore.isSeekSatisfiedAtTime(time)
  ) {
    clearProgressBarSeekDriving();
    transcriptChunkStore.clearUserScrollNavigation();
    transcriptChunkStore.clearScrollPinnedChunk();
    transcriptChunkStore.markProgrammaticScroll(400);
    const local = getBlocksForHighlightAtTime(time);
    const hit = resolvePlaybackHit(time, local);
    if (hit) {
      applyHighlightFromHit(hit, {
        allowScroll: opts?.isFollowing !== false,
        skipScrollIfVisible: true,
        scrollBehavior: 'auto',
      });
    }
    transcriptChunkStore.completeInWindowPlaybackSeek();
    endChunkSeekPerf({
      time,
      path: 'in-window',
      skippedLoad: true,
    });
    return;
  }
  flushPlaybackHighlight(time, { ...opts, seek: true });
};

/**
 * Smooth highlight while media is playing. Prefer this over timeupdate —
 * media timeupdate is sparse (~4Hz) and misses short words.
 */
export const startPlaybackHighlightLoop = (opts?: {
  isFollowing?: boolean;
}) => {
  resolveFollow(opts);
  if (playLoopRaf) return;

  const tick = () => {
    playLoopRaf = 0;
    const video = (window as any).Video as
      | HTMLMediaElement
      | undefined
      | null;
    if (!video || video.paused || video.ended) return;

    runSync(video.currentTime || 0);
    playLoopRaf = requestAnimationFrame(tick);
  };

  playLoopRaf = requestAnimationFrame(tick);
};

export const stopPlaybackHighlightLoop = () => {
  if (playLoopRaf) {
    cancelAnimationFrame(playLoopRaf);
    playLoopRaf = 0;
  }
};

export const resetPlaybackHighlight = () => {
  stopPlaybackHighlightLoop();
  cancelScheduleRaf();
  cache.captionIndex = 0;
  cache.wordIndex = 0;
  cache.captionStart = null;
  cache.wordStart = null;
  lastActiveChunkId = null;
  lastFollowOrdinal = null;
  setHighlightRules(null, null);
  syncPlaybackPlayingAttr(false);
};

/**
 * After a playback-driven chunk mount: re-sync highlight against the new
 * editor. Scroll the current caption into view only while playing (or after
 * an explicit progress-bar seek / line play) — not when the user scrolled
 * to load the next chunk while paused.
 */
export const syncPlaybackHighlightAfterChunkReady = (opts?: {
  /** Progress-bar seek finished — honor highlight even if user-scroll was active. */
  afterSeek?: boolean;
}) => {
  const editorRevision = transcriptChunkStore.getEditorReadyRevision();
  if (lastChunkReadyHighlightRevision === editorRevision) {
    return;
  }

  const video = (window as any).Video as HTMLMediaElement | undefined | null;

  const linePlayPending = pendingLinePlayScroll;
  if (linePlayPending) {
    pendingLinePlayScroll = false;
  } else if (
    !opts?.afterSeek &&
    shouldSuppressPlaybackFollowScroll() &&
    !isEditModeActive()
  ) {
    return;
  }

  lastChunkReadyHighlightRevision = editorRevision;

  if (opts?.afterSeek) {
    transcriptChunkStore.clearUserScrollNavigation();
    transcriptChunkStore.clearScrollPinnedChunk();
  }

  lastActiveChunkId = transcriptChunkStore.getActiveChunkId();
  lastFollowOrdinal = null;
  cache.captionIndex = 0;
  cache.wordIndex = 0;
  // Keep caption cache when the playhead is still on the same row — avoids a
  // spurious follow-scroll when loading finishes and the row is already visible.
  const time = video?.currentTime ?? 0;
  const blocks = getBlocksForHighlightAtTime(time);
  const nextHit = blocks?.length ? resolvePlaybackHit(time, blocks) : null;
  if (!nextHit || nextHit.captionStart !== cache.captionStart) {
    cache.captionStart = null;
    cache.wordStart = null;
  }

  const useLinePlay =
    linePlayPending && !transcriptChunkStore.isSeekSatisfiedAtTime(time);
  runSync(
    time,
    useLinePlay
      ? { isFollowing: resolveIsFollowingPref(), linePlay: true }
      : {
          chunkReady: true,
          afterSeek: !!opts?.afterSeek,
        },
  );
};

/**
 * Resume media after a load that paused playback — only when the window is
 * editor-ready and seek is idle. Safe to call from mount rAF and when shell
 * wait-lock / pipeline clears (retries until consumed).
 */
export const tryResumePlaybackAfterChunkReady = (): boolean => {
  if (!transcriptChunkStore.wasPlaybackPausedForLoad()) return false;
  if (!transcriptChunkStore.isTranscriptPlayable()) return false;

  const video = (window as any).Video as HTMLMediaElement | undefined | null;
  if (!video?.paused || video.ended) return false;

  const chunkId =
    transcriptChunkStore.getPlaybackResumeChunkId() ??
    transcriptChunkStore.getActiveChunkId();
  if (!chunkId) return false;
  if (!transcriptChunkStore.consumePlaybackResumeForChunk(chunkId)) {
    return false;
  }
  void video.play()?.catch(() => undefined);
  chunkPerfLog('playback:resume-after-ready', { chunkId });
  return true;
};

/** First open: highlight playhead caption at transcript start even before Video attaches. */
export const syncBootTranscriptPresentation = (time = 0) => {
  applyImmediateSeekHighlight(time);
  runSync(time, { chunkReady: true, isFollowing: false });
};

export const setPlaybackHighlightCaptionHandler = (
  handler: typeof onCaptionChange,
) => {
  onCaptionChange = handler;
};

export const setPlaybackHighlightEditModeGetter = (
  getter: (() => boolean) | null,
) => {
  getIsEditMode = getter;
};

export const setPlaybackHighlightFollowingGetter = (
  getter: (() => boolean) | null,
) => {
  getIsFollowing = getter;
};
